import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import { useState } from 'react';

export default function PrivacyPolicyPage() {
  const [contactFormOpen, setContactFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation onOpenContactForm={() => setContactFormOpen(true)} />
      <ContactFormDialog open={contactFormOpen} onOpenChange={setContactFormOpen} />

      <div className="pt-32 pb-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="privacy-policy">
        <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Privacy Policy
        </h1>
        <div className="text-sm text-slate-600 mb-8">Last Updated: January 2026</div>

        <div className="space-y-8 text-base leading-relaxed text-slate-700">
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              1. Introduction
            </h2>
            <p>
              Welcome to Trademark Company ("Company," "we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our trademark registration services, copyright registration services, company formation services, trademark search services, and Amazon Brand Registry services.
            </p>
            <p className="mt-4">
              By accessing or using our services, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              2. Information We Collect
            </h2>
            <h3 className="text-xl font-medium text-slate-900 mb-3 mt-4">Personal Information</h3>
            <p>
              We collect personal information that you voluntarily provide to us when you register for our trademark services, express an interest in obtaining information about us or our services, or otherwise contact us. The personal information we collect may include:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number and country code</li>
              <li>Business name and address</li>
              <li>Trademark information and intellectual property details</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Any other information you choose to provide in forms or communications</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-900 mb-3 mt-6">Automatically Collected Information</h3>
            <p>
              When you visit our website, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. We also collect information about your browsing actions and patterns, including the pages you view, referring/exit pages, and date/time stamps.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              3. How We Use Your Information
            </h2>
            <p>We use the information we collect for various purposes, including:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Providing, operating, and maintaining our trademark registration and intellectual property services</li>
              <li>Processing your trademark applications and filings with the USPTO</li>
              <li>Communicating with you about your trademark status, updates, and service-related matters</li>
              <li>Responding to your inquiries and providing customer support</li>
              <li>Sending you newsletters, marketing communications, and promotional materials (with your consent)</li>
              <li>Improving our website and services based on user feedback and analytics</li>
              <li>Detecting, preventing, and addressing technical issues and security threats</li>
              <li>Complying with legal obligations and protecting our legal rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              4. Disclosure of Your Information
            </h2>
            <p>We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>With Service Providers:</strong> We may share your information with third-party service providers who perform services on our behalf, such as payment processing, data analysis, email delivery, and customer service.</li>
              <li><strong>With Government Agencies:</strong> We share trademark application information with the United States Patent and Trademark Office (USPTO) as required for federal trademark registration.</li>
              <li><strong>For Legal Purposes:</strong> We may disclose your information if required by law, court order, or governmental regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business, your information may be transferred.</li>
              <li><strong>With Your Consent:</strong> We may share your information with third parties when you have given us explicit consent to do so.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              5. Data Security
            </h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include secure socket layer (SSL) encryption, firewalls, access controls, and secure data storage. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              6. Data Retention
            </h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Trademark registration records and related documentation are retained in accordance with USPTO requirements and legal obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              7. Your Privacy Rights
            </h2>
            <p>Depending on your location, you may have certain rights regarding your personal information:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong>Access:</strong> You have the right to request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You have the right to request correction of inaccurate or incomplete personal information.</li>
              <li><strong>Deletion:</strong> You have the right to request deletion of your personal information, subject to certain legal exceptions.</li>
              <li><strong>Opt-Out:</strong> You have the right to opt out of receiving marketing communications from us.</li>
              <li><strong>Data Portability:</strong> You have the right to receive your personal information in a structured, commonly used format.</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at Info@trademarkcompanylogo.com or +1 (123) 123-4567.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              8. Cookies and Tracking Technologies
            </h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with a small amount of data that are sent to your browser from a website and stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              9. Third-Party Links
            </h2>
            <p>
              Our website may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services. We encourage you to review the privacy policy of every site you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              10. Children's Privacy
            </h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will delete such information from our systems.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              11. Changes to This Privacy Policy
            </h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              12. Contact Us
            </h2>
            <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <div className="mt-4 space-y-2">
              <p><strong>Trademark Company</strong></p>
              <p>19th Floors, 515 Flower St, Los Angeles, CA 90071</p>
              <p>Email: Info@trademarkcompanylogo.com</p>
              <p>Phone: +1 (123) 123-4567</p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
